import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, ArrowRight, ArrowLeft, FileDown, Loader2, Eye, EyeOff,
  GraduationCap, Lightbulb, Rocket, Sparkles, Megaphone, Box,
  Cpu, Languages, Briefcase, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

import Brand from "../components/Brand";
import { api } from "../lib/api";
import { EignungStep, useEignungQuestions, isEignungComplete } from "../components/EignungStep";

const STEPS = ["Programm", "Module", "Daten", "Termin", "Eignung", "Bestätigung"];

// Icon per course key
const COURSE_ICONS = {
  "projektmanagement-ki": Cpu,
  "digitales-marketing-ki": Megaphone,
  "design-mit-ki": Sparkles,
  "3d-visualisierung": Box,
  "coaching-modul-1": UserCheck,
  "coaching-modul-2": Languages,
  "coaching-modul-3": Briefcase,
  "gruendercoaching-120": Rocket,
};

export default function PublicForm() {
  // Deep-link via ?track=… is parsed on mount via lazy initial state below.
  const [form, setForm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("track");
    const src = params.get("utm_source") || params.get("src");
    const trackOk = t === "weiterbildung" || t === "coaching" || t === "gruendercoaching";
    return {
      track: trackOk ? t : "",
      full_name: "",
      email: "",
      phone: "",
      city: "",
      customer_number: "",
      selected_courses: [],
      start_date: "",
      has_voucher: false,
      voucher_number: "",
      message: "",
      eignung_answers: {},
      source: src ? `web-${src}` : "website",
    };
  });

  const [step, setStep] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("track");
    return t === "weiterbildung" || t === "coaching" || t === "gruendercoaching" ? 1 : 0;
  });

  const [tracks, setTracks] = useState([]);
  const eignungQuestions = useEignungQuestions();
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);

  // PDF-Preview state
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const prevBlobRef = useRef("");

  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post("/leads/preview", form, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
      prevBlobRef.current = url;
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (e) {
      toast.error("Vorschau konnte nicht erstellt werden");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (prevBlobRef.current) URL.revokeObjectURL(prevBlobRef.current);
    };
  }, []);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  const activeTrack = tracks.find((t) => t.key === form.track);
  const maxCourses =
    form.track === "weiterbildung" ? 2 : form.track === "gruendercoaching" ? 1 : 3;

  const chooseTrack = (key) => {
    setForm((f) => ({ ...f, track: key, selected_courses: [], eignung_answers: {} }));
  };

  const toggleCourse = (key) => {
    setForm((f) => {
      const exists = f.selected_courses.includes(key);
      let next = exists
        ? f.selected_courses.filter((k) => k !== key)
        : [...f.selected_courses, key];
      if (next.length > maxCourses) next = next.slice(-maxCourses);
      return { ...f, selected_courses: next };
    });
  };

  const canNext = () => {
    if (step === 0) return !!form.track;
    if (step === 1) return form.selected_courses.length >= 1;
    if (step === 2)
      return (
        form.full_name.trim().length > 2 &&
        /.+@.+\..+/.test(form.email) &&
        form.phone.trim().length >= 5 &&
        form.city.trim().length >= 2
      );
    if (step === 3) return true;
    if (step === 4) return isEignungComplete(eignungQuestions, form.track, form.eignung_answers);
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", form);
      setLead(data);
      setStep(5);
      toast.success("Angebot wird per E-Mail zugesendet.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Bitte prüfe deine Eingaben.";
      toast.error(typeof msg === "string" ? msg : "Fehler beim Senden");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({
      track: "",
      full_name: "",
      email: "",
      phone: "",
      city: "",
      customer_number: "",
      selected_courses: [],
      start_date: "",
      has_voucher: false,
      voucher_number: "",
      message: "",
      eignung_answers: {},
      source: "website",
    });
    setLead(null);
    setStep(0);
  };

  return (
    <div className="App">
      <Brand />

      {/* Hero blobs */}
      <div className="relative overflow-hidden">
        <div className="blob blob-teal w-96 h-96 -top-32 -left-32 animate-float" />
        <div className="blob blob-mint w-80 h-80 top-40 right-0 animate-float-delay" />
        <div className="blob blob-yellow w-56 h-56 bottom-20 left-1/3" />

        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16 relative z-10">
          {/* Hero */}
          <section className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--teal-light)] border border-[color:var(--teal)]/30 text-xs font-semibold text-[color:var(--teal-dark)] mb-6">
                <Sparkles size={14} /> AZAV zertifiziert · 100% förderfähig
              </div>              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight">
                Dein persönliches{" "}
                <span className="underline-grad text-gradient">Angebot</span>
                <br />
                in unter 60&nbsp;Sekunden.
              </h1>
              <p className="mt-6 text-base md:text-lg max-w-xl text-[color:var(--grey)]">
                Wähle Weiterbildung oder Jobcoaching, deine Module und hinterlege
                deine Daten — du erhältst sofort ein personalisiertes Angebot
                per E-Mail. 100% förderfähig über Bildungsgutschein oder AVGS.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {["100% Remote / Hybrid", "AVGS & Bildungsgutschein", "Coaches aus der Kreativbranche"].map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-white border border-[color:var(--border)] text-xs font-semibold">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="surface p-6">
                <p className="label-mono mb-3">Schritt {step + 1} von {STEPS.length}</p>
                <div className="flex gap-3 mb-5">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1">
                      <div
                        data-testid={`step-dot-${i}`}
                        className={`step-dot ${i < step ? "done" : ""} ${i === step ? "active" : ""}`}
                      />
                      <p className="mt-2 label-mono text-[10px]">{s}</p>
                    </div>
                  ))}
                </div>
                <div className="font-display text-xl font-bold">
                  {step === 0 && "Weiterbildung oder Coaching?"}
                  {step === 1 && (activeTrack
                    ? (activeTrack.key === "gruendercoaching"
                        ? `${activeTrack.label} — bestätige deine Maßnahme`
                        : `${activeTrack.label} — wähle deine Module`)
                    : "Wähle deine Module")}
                  {step === 2 && "Wie dürfen wir dich erreichen?"}
                  {step === 3 && "Wann möchtest du starten?"}
                  {step === 4 && "Kurze Eignungsfeststellung"}
                  {step === 5 && "Erledigt — wir freuen uns auf dich."}
                </div>
                {activeTrack && step > 0 && activeTrack.measure_id && (
                  <p className="mt-2 label-mono text-[color:var(--teal-dark)]">
                    Maßnahme {activeTrack.measure_id} · {activeTrack.measure_type}
                  </p>
                )}
                {activeTrack && step > 0 && !activeTrack.measure_id && (
                  <p className="mt-2 label-mono text-[color:var(--teal-dark)]">
                    {activeTrack.measure_type}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Form Card */}
          <section data-testid="form-section" className="surface p-6 md:p-12">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="s0"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display text-2xl sm:text-3xl mb-2 bar-boost font-bold">
                    Was möchtest du machen?
                  </h2>
                  <p className="text-[color:var(--grey)] mb-10">
                    Wähle deinen Förderweg — Weiterbildung mit Bildungsgutschein
                    oder Jobcoaching mit AVGS.
                  </p>
                  <div className="grid md:grid-cols-3 gap-6">
                    <button
                      type="button"
                      data-testid="track-weiterbildung"
                      onClick={() => chooseTrack("weiterbildung")}
                      className={`track-card ${form.track === "weiterbildung" ? "active" : ""}`}
                    >
                      <div className="track-icon">
                        <GraduationCap size={28} />
                      </div>
                      <h3 className="font-display text-2xl font-bold mb-2">Weiterbildung</h3>
                      <p className="text-sm text-[color:var(--grey)] mb-4">
                        4 Module — Projektmanagement mit KI (180 UE),
                        Digitales Marketing (385 UE), Design mit KI (385 UE) oder
                        3D-Visualisierung (385 UE).
                      </p>
                      <div className="flex items-center gap-2 label-mono text-[color:var(--teal-dark)]">
                        <span>§ 81 SGB III · Bildungsgutschein</span>
                      </div>
                      {form.track === "weiterbildung" && (
                        <Check className="absolute top-6 right-6 text-[color:var(--teal-dark)]" size={22} />
                      )}
                    </button>

                    <button
                      type="button"
                      data-testid="track-coaching"
                      onClick={() => chooseTrack("coaching")}
                      className={`track-card ${form.track === "coaching" ? "active" : ""}`}
                    >
                      <div className="track-icon">
                        <Lightbulb size={28} />
                      </div>
                      <h3 className="font-display text-2xl font-bold mb-2">Jobcoaching</h3>
                      <p className="text-sm text-[color:var(--grey)] mb-4">
                        3 Module (80–150 UE) — Orientierung, Profiling & Bewerbung
                        (Modul 1), Kommunikation für Bewerbung und Beruf (Modul 2),
                        Portfolio & Softwarekompetenzen (Modul 3).
                      </p>
                      <div className="flex items-center gap-2 label-mono text-[color:var(--teal-dark)]">
                        <span>§ 45 SGB III · AVGS-MAT</span>
                      </div>
                      {form.track === "coaching" && (
                        <Check className="absolute top-6 right-6 text-[color:var(--teal-dark)]" size={22} />
                      )}
                    </button>

                    <button
                      type="button"
                      data-testid="track-gruendercoaching"
                      onClick={() => chooseTrack("gruendercoaching")}
                      className={`track-card ${form.track === "gruendercoaching" ? "active" : ""}`}
                    >
                      <div className="track-icon">
                        <Rocket size={28} />
                      </div>
                      <h3 className="font-display text-2xl font-bold mb-2">Gründercoaching</h3>
                      <p className="text-sm text-[color:var(--grey)] mb-4">
                        Einzelcoaching mit 120 UE — Businessplan & Positionierung,
                        Marketing & Kundenakquise, Finanzen & Recht für Kreative
                        in der Selbständigkeit.
                      </p>
                      <div className="flex items-center gap-2 label-mono text-[color:var(--teal-dark)]">
                        <span>§ 45 SGB III · AVGS-MAT (Gründung)</span>
                      </div>
                      {form.track === "gruendercoaching" && (
                        <Check className="absolute top-6 right-6 text-[color:var(--teal-dark)]" size={22} />
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 1 && activeTrack && (
                <motion.div
                  key="s1"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display text-2xl sm:text-3xl mb-2 bar-boost font-bold">
                    {form.track === "gruendercoaching"
                      ? "Bestätige deine Maßnahme"
                      : `Wähle deine Module (max. ${maxCourses})`}
                  </h2>
                  <p className="text-[color:var(--grey)] mb-8">
                    {form.track === "weiterbildung"
                      ? "Wähle 1–2 Module. Projektmanagement mit KI = 180 UE, die anderen jeweils 385 UE — alle über den Bildungsgutschein förderfähig."
                      : form.track === "gruendercoaching"
                      ? "Das Gründercoaching umfasst 120 UE als ganzheitliches Einzelcoaching — Businessplan, Marketing und Finanzen für Kreative in der Selbständigkeit."
                      : "Wähle 1–3 Coaching-Module. Modul 1 (80 UE) + Modul 2 (150 UE) + Modul 3 (150 UE) — individuell zusammenstellbar."}
                  </p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {activeTrack.courses.map((c) => {
                      const active = form.selected_courses.includes(c.key);
                      const Icon = COURSE_ICONS[c.key] || Sparkles;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          data-testid={`course-${c.key}`}
                          onClick={() => toggleCourse(c.key)}
                          className={`tile text-left ${active ? "active" : ""}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="tile-icon">
                              <Icon size={20} />
                            </div>
                            {active && <Check size={20} />}
                          </div>
                          <h3 className="font-display text-lg font-bold leading-tight tile-text">
                            {c.name}
                          </h3>
                          <p className="tile-ue label-mono mt-2">{c.ue} UE</p>
                          <p className="text-sm mt-3 tile-text opacity-80">{c.lernziel}</p>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="s2"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display text-2xl sm:text-3xl mb-2 bar-boost font-bold">
                    Deine Kontaktdaten
                  </h2>
                  <p className="text-[color:var(--grey)] mb-8">
                    Wir senden das Angebot direkt an die hinterlegte E-Mail.
                    Telefon nutzen wir nur für die Pre-Qualifizierung.
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label className="label-mono">Vollständiger Name *</label>
                      <input
                        className="input-clean mt-2"
                        data-testid="input-name"
                        placeholder="Max Mustermann"
                        value={form.full_name}
                        onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-mono">E-Mail *</label>
                      <input
                        className="input-clean mt-2"
                        data-testid="input-email"
                        type="email"
                        placeholder="max@beispiel.de"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-mono">Telefon *</label>
                      <input
                        className="input-clean mt-2"
                        data-testid="input-phone"
                        type="tel"
                        placeholder="+49 …"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-mono">Stadt *</label>
                      <input
                        className="input-clean mt-2"
                        data-testid="input-city"
                        placeholder="z.B. Berlin"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-mono">Kundennummer (optional)</label>
                      <input
                        className="input-clean mt-2"
                        data-testid="input-customer-number"
                        placeholder="z.B. 123456789 — falls schon vorhanden"
                        value={form.customer_number}
                        onChange={(e) => setForm({ ...form, customer_number: e.target.value })}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  key="s3"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display text-2xl sm:text-3xl mb-2 bar-boost font-bold">
                    Wunsch-Starttermin
                  </h2>
                  <p className="text-[color:var(--grey)] mb-8">
                    {form.track === "coaching"
                      ? "Laufender Einstieg — Start jederzeit möglich."
                      : "Wir bestätigen den genauen Termin telefonisch in der Pre-Qualifizierung."}
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label className="label-mono">Wunsch-Starttermin</label>
                      <input
                        type="date"
                        className="input-clean mt-2"
                        data-testid="input-start-date"
                        value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="label-mono">
                        {form.track === "weiterbildung"
                          ? "Bildungsgutschein bereits vorhanden?"
                          : "AVGS-Gutschein bereits vorhanden?"}
                      </label>
                      <div className="mt-2 flex gap-3">
                        <button
                          type="button"
                          data-testid="voucher-yes"
                          onClick={() => setForm({ ...form, has_voucher: true })}
                          className={`flex-1 py-3 rounded-2xl border-2 font-semibold transition ${
                            form.has_voucher
                              ? "border-[color:var(--teal)] bg-[color:var(--teal-light)] text-[color:var(--teal-dark)]"
                              : "border-[color:var(--border)] hover:border-[color:var(--teal)]/50"
                          }`}
                        >
                          Ja, habe ich
                        </button>
                        <button
                          type="button"
                          data-testid="voucher-no"
                          onClick={() => setForm({ ...form, has_voucher: false, voucher_number: "" })}
                          className={`flex-1 py-3 rounded-2xl border-2 font-semibold transition ${
                            !form.has_voucher
                              ? "border-[color:var(--teal)] bg-[color:var(--teal-light)] text-[color:var(--teal-dark)]"
                              : "border-[color:var(--border)] hover:border-[color:var(--teal)]/50"
                          }`}
                        >
                          Noch nicht
                        </button>
                      </div>
                    </div>
                    {form.has_voucher && (
                      <div className="md:col-span-2">
                        <label className="label-mono">Gutschein-Nummer (optional)</label>
                        <input
                          className="input-clean mt-2"
                          data-testid="input-voucher-number"
                          placeholder="z.B. AVGS-Nummer oder Bildungsgutschein-Nr."
                          value={form.voucher_number}
                          onChange={(e) => setForm({ ...form, voucher_number: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="label-mono">Anmerkung (optional)</label>
                      <textarea
                        rows={4}
                        className="input-clean mt-2 resize-none"
                        data-testid="input-message"
                        placeholder="z.B. besondere Vorkenntnisse, Wunschmodul-Kombination …"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="mt-8 p-5 rounded-2xl bg-[color:var(--teal-light)] border border-[color:var(--teal)]/20">
                    <p className="label-mono mb-2 text-[color:var(--teal-dark)]">Zusammenfassung</p>
                    <p className="font-display text-lg font-bold">
                      {activeTrack?.label} · {form.full_name || "—"}
                    </p>
                    <p className="text-sm mt-1 text-[color:var(--grey)]">
                      {form.email || "—"} · {form.phone || "—"} · {form.city || "—"}
                    </p>
                    <p className="text-sm mt-2 text-[color:var(--grey)]">
                      Module: {form.selected_courses
                        .map((k) => activeTrack?.courses.find((c) => c.key === k)?.name)
                        .join(" + ") || "—"}
                    </p>
                    <p className="text-sm mt-2 text-[color:var(--grey)]" data-testid="summary-voucher">
                      {form.track === "weiterbildung" ? "Bildungsgutschein" : "AVGS-Gutschein"}:{" "}
                      {form.has_voucher
                        ? `Ja${form.voucher_number ? ` · Nr. ${form.voucher_number}` : ""}`
                        : "noch nicht vorhanden"}
                    </p>
                  </div>

                  {/* ── PDF Live-Vorschau ─────────────────────────────── */}
                  <div className="mt-6">
                    <button
                      type="button"
                      data-testid="pdf-preview-toggle"
                      onClick={() => (showPreview ? setShowPreview(false) : generatePreview())}
                      disabled={previewLoading || form.selected_courses.length === 0}
                      className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl
                                 border-2 border-dashed border-[color:var(--teal)]/40
                                 bg-white hover:bg-[color:var(--teal-light)]
                                 text-[color:var(--teal-dark)] font-semibold transition
                                 disabled:opacity-50"
                    >
                      {previewLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Vorschau wird generiert …
                        </>
                      ) : showPreview ? (
                        <>
                          <EyeOff size={18} /> Vorschau ausblenden
                        </>
                      ) : (
                        <>
                          <Eye size={18} /> Wie sieht mein Angebot aus?
                        </>
                      )}
                    </button>

                    <AnimatePresence>
                      {showPreview && previewUrl && (
                        <motion.div
                          key="preview"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.35 }}
                          className="overflow-hidden mt-4"
                          data-testid="pdf-preview-frame"
                        >
                          <div className="rounded-2xl border-2 border-[color:var(--teal)]/30
                                          bg-[color:var(--light)] p-2 shadow-lg">
                            <iframe
                              src={previewUrl}
                              title="Angebot Vorschau"
                              className="w-full rounded-xl bg-white"
                              style={{ height: "620px", border: 0 }}
                            />
                          </div>
                          <p className="mt-2 text-xs text-center text-[color:var(--grey)]">
                            💡 Das ist eine Live-Vorschau. Klick auf &bdquo;Angebot absenden&ldquo;,
                            um es dir per E-Mail zuschicken zu lassen.{" "}
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="underline text-[color:var(--teal-dark)] hover:opacity-80"
                              data-testid="pdf-preview-newtab"
                            >
                              In neuem Tab öffnen
                            </a>
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div
                  key="s4"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 className="font-display text-2xl sm:text-3xl mb-2 bar-boost font-bold">
                    Eignungsfeststellung
                  </h2>
                  <p className="text-[color:var(--grey)] mb-8">
                    Bitte beantworte die folgenden Fragen. Danach kannst du dein Angebot sofort herunterladen.
                  </p>
                  <EignungStep
                    questionsByTrack={eignungQuestions}
                    track={form.track}
                    answers={form.eignung_answers}
                    onChange={(a) => setForm((f) => ({ ...f, eignung_answers: a }))}
                  />
                </motion.div>
              )}

              {step === 5 && lead && (
                <motion.div
                  key="s5"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="text-center py-8"
                  data-testid="success-screen"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--teal-light)] border border-[color:var(--teal)]/30 label-mono text-[color:var(--teal-dark)] mb-6">
                    <Check size={14} /> Bestätigt
                  </div>
                  <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight">
                    Dein Angebot ist <span className="text-gradient">unterwegs.</span>
                  </h2>
                  <p className="mt-5 max-w-xl mx-auto text-[color:var(--grey)]">
                    Wir haben dein personalisiertes Angebot generiert und an{" "}
                    <strong>{lead.email}</strong> versendet. Du kannst es jetzt
                    schon direkt herunterladen.
                  </p>
                  <div className="mt-8 flex justify-center gap-3 flex-wrap">
                    <a
                      href={lead.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="download-pdf-link"
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      <FileDown size={18} /> PDF herunterladen
                    </a>
                    <button
                      className="btn-secondary"
                      data-testid="new-offer-btn"
                      onClick={reset}
                    >
                      Neues Angebot
                    </button>
                  </div>
                  <p className="mt-8 label-mono text-[color:var(--grey)]">
                    Lead-ID: {lead.id}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {step < 5 && (
              <div className="mt-10 flex items-center justify-between border-t border-[color:var(--border)] pt-6">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="prev-step-btn"
                  className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
                >
                  <ArrowLeft size={16} /> Zurück
                </button>
                {step < 4 ? (
                  <button
                    type="button"
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canNext()}
                    data-testid="next-step-btn"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Weiter <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting || !canNext()}
                    data-testid="submit-btn"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={16} /> Wird gesendet …
                      </>
                    ) : (
                      <>
                        Angebot anfordern <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </section>

          {/* Footer info */}
          <footer className="mt-20 border-t border-[color:var(--border)] pt-8 grid md:grid-cols-3 gap-6 label-mono text-[color:var(--grey)]">
            <div>
              <p className="text-black font-bold">We Academy of Arts and Tech</p>
              <p>Fasanenstraße 85 · 10623 Berlin</p>
            </div>
            <div>
              <p>info@we-academy.de</p>
              <p>+49 030 16 63 92 08</p>
            </div>
            <div className="md:text-right">
              <p>AZAV zertifiziert · CERTQUA</p>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
